import { ErrorMessage, Field, Formik, Form as FormikForm } from 'formik';
import { useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { IoIosArrowRoundBack } from 'react-icons/io';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import * as Yup from 'yup';
import './GroupInformationForm.scss';
import { State } from 'country-state-city';
import Select from 'react-select';
import GroupAddressAutocomplete from './GroupAddressAutoComplete';
import PhoneInput from 'react-phone-input-2';

const GroupInformationForm = ({ changeCurrentStep, step0Values, setStep0Values }) => {
    const { loading } = useSelector((state) => state.auth);
    const [states, setStates] = useState([]);

    useEffect(() => {
        // Fetch US states
        const usStates = State.getStatesOfCountry('US');
        const stateOptions = usStates.map((state) => ({
            value: state.name,
            label: state.name
        }));
        setStates(stateOptions);
    }, []);

    const initialValues = {
        groupName: step0Values.groupName,
        streetAddress: step0Values.streetAddress,
        streetAddress2: step0Values.streetAddress2,
        city: step0Values.city,
        state: step0Values.state,
        zipCode: step0Values.zipCode,
        firstName: step0Values.firstName,
        lastName: step0Values.lastName,
        title: step0Values.title,
        email: step0Values.email,
        phoneNumber: step0Values.phoneNumber,
        notifications: step0Values.notifications
    };

    const validationSchema = Yup.object().shape({
        groupName: Yup.string().required('Group Name is required'),
        streetAddress: Yup.string().required('Street Address is required'),
        city: Yup.string().required('City is required'),
        state: Yup.string().required('State is required'),
        zipCode: Yup.string().required('Zip Code is required'),
        firstName: Yup.string().required('First Name is required'),
        lastName: Yup.string().required('Last Name is required'),
        title: Yup.string().required('Title is required'),
        email: Yup.string().email('Invalid email').required('Email Address is required'),
        phoneNumber: Yup.string().required('Phone Number is required'),
        notifications: Yup.boolean()
    });

    const handleSubmit = async (values, { setSubmitting }) => {
        try {
            setStep0Values({ ...values, isCompleted: true });
            changeCurrentStep(1, false);
            setSubmitting(false);
        } catch (error) {
            setSubmitting(false);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Formik
            initialValues={initialValues}
            enableReinitialize
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            validateOnChange={true}
        >
            {({ isSubmitting, setFieldValue, values, errors, touched }) => (
                <FormikForm className="group-information-form">
                    <div className="details-heading">General Info</div>
                    <div className="fields-row">
                        <div className="field-container">
                            <label htmlFor="groupName">Group Name*</label>
                            <Field name="groupName" placeholder="Enter Group Name" type="text" />
                            <ErrorMessage name="groupName" component="span" className="validation-error" />
                        </div>
                        <div className="field-container">
                            <label htmlFor="streetAddress">Street Address*</label>
                            <GroupAddressAutocomplete
                                setFieldValue={setFieldValue}
                                values={values}
                                errors={errors}
                                touched={touched}
                            />
                            <ErrorMessage name="streetAddress" component="span" className="validation-error" />
                        </div>
                        <div className="field-container">
                            <label htmlFor="streetAddress2">Street Address 2</label>
                            <Field name="streetAddress2" placeholder="Enter Street Address 2" type="text" />
                        </div>
                    </div>

                    <div className="fields-row">
                        <div className="field-container">
                            <label htmlFor="city">City*</label>
                            <Field name="city" placeholder="Enter City" type="text" />
                            <ErrorMessage name="city" component="span" className="validation-error" />
                        </div>
                        <div className="field-container">
                            <label htmlFor="state">State*</label>
                            <Select
                                name="state"
                                options={states}
                                className="multi-select-field"
                                classNamePrefix="__select-days"
                                placeholder="Select state"
                                value={states.find((state) => state.value === values.state) || null}
                                onChange={(selectedOption) => setFieldValue('state', selectedOption.value)}
                            />
                            <ErrorMessage name="state" component="span" className="validation-error" />
                        </div>
                        <div className="field-container">
                            <label htmlFor="zipCode">Zip Code*</label>
                            <Field name="zipCode" placeholder="Enter Zip Code" type="text" />
                            <ErrorMessage name="zipCode" component="span" className="validation-error" />
                        </div>
                    </div>

                    <div className="details-heading">Primary Contact</div>
                    <div className="fields-row">
                        <div className="field-container">
                            <label htmlFor="firstName">First Name*</label>
                            <Field name="firstName" placeholder="Enter First Name" type="text" />
                            <ErrorMessage name="firstName" component="span" className="validation-error" />
                        </div>
                        <div className="field-container">
                            <label htmlFor="lastName">Last Name*</label>
                            <Field name="lastName" placeholder="Enter Last Name" type="text" />
                            <ErrorMessage name="lastName" component="span" className="validation-error" />
                        </div>
                        <div className="field-container">
                            <label htmlFor="title">Title*</label>
                            <Field name="title" placeholder="Enter Title" type="text" />
                            <ErrorMessage name="title" component="span" className="validation-error" />
                        </div>
                    </div>

                    <div className="fields-row">
                        <div className="field-container">
                            <label htmlFor="email">Email Address*</label>
                            <Field name="email" placeholder="Enter Email Address" type="email" />
                            <ErrorMessage name="email" component="span" className="validation-error" />
                        </div>
                        <div className="field-container">
                            <label htmlFor="phoneNumber">Phone Number*</label>
                            {/* <Field name="phoneNumber" placeholder="Enter Phone Number" type="text" /> */}
                            <PhoneInput
                                // onlyCountries={['us', 'pk']}
                                country={'us'}
                                value={initialValues.phoneNumber}
                                onChange={(phone) => setFieldValue('phoneNumber', `+${phone}`)}
                                placeholder="Enter your mobile number"
                                inputClass="form-input"
                                containerClass="container-phone"
                                countryCodeEditable={false}
                                enableSearch={true}
                                searchPlaceholder="Search"
                            />
                            <ErrorMessage name="phoneNumber" component="span" className="validation-error" />
                        </div>
                    </div>

                    <div className="notification-toggle">
                        <label className="switch">
                            <Field type="checkbox" name="notifications" />
                            <span className="slider"></span>
                        </label>
                        <span className="switch-label">Notifications</span>
                    </div>

                    <div className="buttons-row">
                        <Link className="back-to-home" to="/admin/groups">
                            <IoIosArrowRoundBack />
                            <span>Back</span>
                        </Link>
                        <button type="submit" className="next-btn" disabled={loading || isSubmitting}>
                            {isSubmitting ? <Spinner animation="border" size="sm" /> : 'Next'}
                        </button>
                    </div>
                </FormikForm>
            )}
        </Formik>
    );
};

export default GroupInformationForm;
